import React, { useState, useEffect } from "react";
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from "styled-components";
import { theme } from '../styles/theme';
import Button from '../components/Button';
import Toast from '../components/Toast';
import { sendEmailMandrill, sendEmailAWS, sendConfirmationMandrill, sendConfirmationAWS } from '../utils/emailService';

const MapFallback = styled.div`
  width: 100%;
  height: 300px;
  background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
  border-radius: ${theme.borderRadius.lg};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.secondary[600]};
  text-align: center;
  padding: ${theme.spacing.xl};
  
  .icon {
    font-size: 48px;
    margin-bottom: ${theme.spacing.md};
  }
  
  h4 {
    margin: 0 0 ${theme.spacing.sm} 0;
    color: ${theme.colors.secondary[700]};
  }
  
  p {
    margin: 0;
    font-size: ${theme.typography.fontSize.sm};
  }
  
  a {
    color: ${theme.colors.primary[600]};
    text-decoration: none;
    font-weight: ${theme.typography.fontWeight.medium};
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const isValidGoogleMapsKey = (key) => {
  return key && key !== "AIzaSyB0-_00000000000000000000000000000000" && key.startsWith("AIza");
};

const PageContainer = styled.div`
  min-height: 80vh;
  padding: ${theme.spacing['4xl']} ${theme.spacing.xl};
  background: ${theme.gradients.hero};
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing['4xl']};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.fontSize['4xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.secondary[900]};
  margin-bottom: ${theme.spacing.lg};
`;

const PageSubtitle = styled.p`
  font-size: ${theme.typography.fontSize.xl};
  color: ${theme.colors.secondary[600]};
  max-width: 600px;
  margin: 0 auto;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing['3xl']};
  
  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const FormContainer = styled.div`
  background: ${theme.colors.white};
  padding: ${theme.spacing['2xl']};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.lg};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.secondary[700]};
`;

const Input = styled.input`
  padding: ${theme.spacing.md};
  border: 2px solid ${theme.colors.secondary[200]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: ${theme.spacing.md};
  border: 2px solid ${theme.colors.secondary[200]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  font-family: inherit;
  resize: vertical;
  min-height: 120px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
  }
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`;

const InfoCard = styled.div`
  background: ${theme.colors.white};
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.md};
  
  h3 {
    color: ${theme.colors.secondary[900]};
    margin-bottom: ${theme.spacing.lg};
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
  
  p {
    display: flex;
    align-items: center;
    gap: ${theme.spacing.sm};
    color: ${theme.colors.secondary[600]};
    margin: 0;
    
    .icon {
      font-size: 18px;
      width: 24px;
    }
  }
`;

const MapContainer = styled.div`
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${theme.shadows.md};
  height: 300px;
`;

/**
 * Componente de página de contacto con formulario de envío de emails
 * 
 * Responsabilidades:
 * - Renderizar formulario y validación
 * - Manejar reCAPTCHA según entorno
 * - Coordinar envío via emailService
 * - Mostrar mensajes de éxito/error
 */
function Contacto() {
  const { t } = useTranslation(['pages', 'common']);
  
  // Determina si el envío de emails está deshabilitado (modo testing)
  const isEmailDisabled = process.env.REACT_APP_SEND_EMAILS === '0';
  
  // Estado del formulario - pre-rellenado en modo testing
  const [formData, setFormData] = useState({
    nombre: isEmailDisabled ? "javier test" : "",
    email: isEmailDisabled ? "javierpoz@gmail.com" : "",
    motivo: isEmailDisabled ? "javi desde local" : "",
    mensaje: isEmailDisabled ? "Lorem ipsum dolor sit amet consectetur adipiscing" : "",
  });
  
  // Estados de control del formulario
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

  /**
   * Efecto para cargar reCAPTCHA según el entorno
   * - Local: Completamente omitido (no carga script)
   * - Staging/Prod: Carga script de Google reCAPTCHA
   */
  useEffect(() => {
    console.log('Environment:', process.env.REACT_APP_ENVIRONMENT);
    
    // En entorno local, omite completamente reCAPTCHA
    if (process.env.REACT_APP_ENVIRONMENT === 'local') {
      console.log('🚫 reCAPTCHA completamente deshabilitado en entorno local');
      
      // Remover cualquier script de reCAPTCHA existente
      const existingScripts = document.querySelectorAll('script[src*="recaptcha"]');
      existingScripts.forEach(script => {
        console.log('Removiendo script reCAPTCHA existente');
        script.remove();
      });
      
      setRecaptchaLoaded(true);
      return;
    }
    
    // Solo carga reCAPTCHA en staging/production
    const loadRecaptcha = () => {
      if (!process.env.REACT_APP_RECAPTCHA_SITE_KEY || process.env.REACT_APP_RECAPTCHA_SITE_KEY === 'your-recaptcha-site-key-here') {
        console.warn('reCAPTCHA site key not configured');
        setRecaptchaLoaded(true);
        return;
      }
      
      // Verificar si ya existe el script para evitar duplicados
      const existingScript = document.querySelector('script[src*="recaptcha"]');
      if (existingScript) {
        if (window.grecaptcha && window.grecaptcha.ready) {
          window.grecaptcha.ready(() => {
            setRecaptchaLoaded(true);
          });
        }
        return;
      }
      
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
      script.onload = () => {
        if (window.grecaptcha) {
          window.grecaptcha.ready(() => {
            setRecaptchaLoaded(true);
          });
        }
      };
      script.onerror = () => {
        console.error('Failed to load reCAPTCHA script');
        setRecaptchaLoaded(true);
      };
      document.head.appendChild(script);
    };
    
    loadRecaptcha();
  }, []);

  /**
   * Maneja los cambios en los campos del formulario
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  /**
   * Obtiene el token de reCAPTCHA para validación
   * - Local: Retorna 'local-bypass' (sin validación)
   * - Prod: Ejecuta reCAPTCHA y retorna token real
   */
  const getRecaptchaToken = async () => {
    // En entorno local, omite reCAPTCHA y retorna bypass
    if (process.env.REACT_APP_ENVIRONMENT === 'local') {
      console.log('🚫 reCAPTCHA disabled in local environment');
      return 'local-bypass';
    }
    
    if (!process.env.REACT_APP_RECAPTCHA_SITE_KEY || process.env.REACT_APP_RECAPTCHA_SITE_KEY === 'your-recaptcha-site-key-here') {
      return null;
    }
    
    if (!window.grecaptcha || !recaptchaLoaded) {
      throw new Error('reCAPTCHA not loaded');
    }
    
    return new Promise((resolve, reject) => {
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, { action: 'contact_form' })
          .then(resolve)
          .catch(reject);
      });
    });
  };

  /**
   * Maneja el envío del formulario
   * Utiliza emailService para el envío real
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const recaptchaToken = await getRecaptchaToken();
      const provider = process.env.REACT_APP_EMAIL_PROVIDER || 'mandrill';
      
      // Envío según proveedor configurado
      if (provider === 'both') {
        console.log('🚀 Enviando con ambos proveedores...');
        const [mandrillResult, awsResult] = await Promise.allSettled([
          sendEmailMandrill(formData, recaptchaToken),
          sendEmailAWS(formData, recaptchaToken)
        ]);
        console.log('Resultados:', { mandrill: mandrillResult, aws: awsResult });
        
        // Enviar confirmación al usuario
        await Promise.allSettled([
          sendConfirmationMandrill(formData),
          sendConfirmationAWS(formData)
        ]);
      } else if (provider === 'aws') {
        await sendEmailAWS(formData, recaptchaToken);
        await sendConfirmationAWS(formData);
      } else {
        await sendEmailMandrill(formData, recaptchaToken);
        await sendConfirmationMandrill(formData);
      }
      
      setToast({ type: 'success', message: t('pages:contact.form.success') });
      
      // Reset form según modo
      if (process.env.REACT_APP_SEND_EMAILS === '0') {
        // Modo testing: mantener datos pre-rellenados
        setFormData({
          nombre: "javier test",
          email: "javierpoz@gmail.com",
          motivo: "javi desde local",
          mensaje: "Lorem ipsum dolor sit amet consectetur adipiscing",
        });
      } else {
        // Modo producción: limpiar formulario
        setFormData({ nombre: "", email: "", motivo: "", mensaje: "" });
      }
      
    } catch (error) {
      console.error('Error enviando email:', error);
      setToast({ 
        type: 'error', 
        message: error?.message || t('pages:contact.form.error') 
      });
    } finally {
      setIsSubmitting(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <PageContainer>
      <Container>
        <PageHeader>
          <PageTitle>{t('pages:contact.title')}</PageTitle>
          <PageSubtitle>{t('pages:contact.subtitle')}</PageSubtitle>
        </PageHeader>

        <ContentGrid>
          <FormContainer>
            <h3 style={{ marginBottom: theme.spacing.lg, color: theme.colors.secondary[900] }}>
              {t('pages:contact.form.submit')}
            </h3>

            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="motivo">{t('pages:contact.form.subject')}</Label>
                <Input
                  type="text"
                  id="motivo"
                  name="motivo"
                  placeholder={t('pages:contact.form.subjectPlaceholder')}
                  value={formData.motivo}
                  onChange={handleChange}
                  maxLength={100}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="nombre">{t('pages:contact.form.name')}</Label>
                <Input
                  type="text"
                  id="nombre"
                  name="nombre"
                  placeholder={t('pages:contact.form.namePlaceholder')}
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">{t('pages:contact.form.email')}</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  placeholder={t('pages:contact.form.emailPlaceholder')}
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="mensaje">{t('pages:contact.form.message')}</Label>
                <TextArea
                  id="mensaje"
                  name="mensaje"
                  placeholder={t('pages:contact.form.messagePlaceholder')}
                  value={formData.mensaje}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <Button type="submit" variant="primary" size="large" disabled={isSubmitting}>
                {isSubmitting ? t('pages:contact.form.sending') : t('pages:contact.form.submit')}
              </Button>
              
              <p 
                style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.secondary[500], marginTop: theme.spacing.sm }}
                dangerouslySetInnerHTML={{ __html: t('pages:contact.form.recaptcha') }}
              />
            </Form>
          </FormContainer>

          <InfoContainer>
            <InfoCard>
              <h3>{t('pages:contact.info.title')}</h3>
              <ContactInfo>
                <p>
                  <span className="icon">📍</span>
                  {t('pages:contact.info.address')}
                </p>

                <p>
                  <span className="icon">📧</span>
                  <span style={{ color: 'inherit' }}>
                    {t('pages:contact.info.email')}
                  </span>
                </p>
                <p>
                  <span className="icon">🕒</span>
                  {t('pages:contact.info.hours')}
                </p>
              </ContactInfo>
            </InfoCard>

            <MapContainer>
              <iframe
                src={process.env.REACT_APP_GOOGLE_MAPS_EMBED_URL}
                width="100%"
                height="300"
                style={{ border: 0, borderRadius: theme.borderRadius.lg }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Madrid, Spain"
              />
            </MapContainer>
          </InfoContainer>
        </ContentGrid>
      </Container>
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}
    </PageContainer>
  );
}

export default Contacto;